import React, {useMemo} from 'react';

import {Handler, RendererOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {ResourceListSource} from '../../../../settings/helpers/resourceTableHooks';
import {AsyncMoreStatusesEnum, asyncMoreStatusToAsyncStatus} from '../../../../store/appTypes';
import {FilterResourceResults, FilterRow} from '../common/types';
import {MultiSelectFilterDropdown} from '../multiSelectFilter/multiSelectFilterDropdown';
import {FilterSourceDropdownProps} from './filterSourceDropdown';

/*
 * Consts.
 */

const baseKey = 'filter-query-dropdown-results';

/*
 * Props.
 */

interface FilterQueryDropdownProps<T> extends Omit<FilterSourceDropdownProps<T>, 'source'> {
  // Data source returned by useResourceListSource hook.
  source: ResourceListSource<T>;
}

/*
 * Component.
 */

/**
 * Sibling of FilterSourceDropdown. This version is specific to useResourceList, and can be used by React Query.
 * See src/settings/helpers/resourceTableHooks.ts
 */
export const FilterQueryDropdown = <T,>(props: FilterQueryDropdownProps<T>) => {
  const {source, filterResourceOf, renderEmpty, title, ...multiSelectFilterDropdown} = props;
  const {items, loadMore, moreStatus, refetch} = source;

  const rows: ReadonlyArray<FilterRow> = useMemo(
    () => items?.map(filterResourceOf) ?? [],
    [filterResourceOf, items],
  );

  const results = useMemo(
    (): FilterResourceResults =>
      computeFilterResourceResults(
        moreStatus,
        refetch,
        loadMore,
        multiSelectFilterDropdown.searchProps?.searchQuery,
        renderEmpty,
      ),
    [moreStatus, refetch, loadMore, multiSelectFilterDropdown.searchProps?.searchQuery, renderEmpty],
  );

  return (
    <MultiSelectFilterDropdown {...multiSelectFilterDropdown} rows={rows} results={results} title={title} />
  );
};

/*
 * Helpers.
 */

function computeFilterResourceResults<T>(
  asyncMoreStatus: AsyncMoreStatusesEnum,
  refetch: Handler,
  loadMore: Handler,
  query?: string,
  renderEmpty?: RendererOf<string>,
): FilterResourceResults {
  const asyncStatus = asyncMoreStatusToAsyncStatus(asyncMoreStatus);

  return {
    key: `${baseKey}-${query}`,
    asyncStatus,
    onLoadingRetryClicked: refetch,
    loadMore,
    canLoadMore: asyncMoreStatus === AsyncMoreStatusesEnum.HAS_MORE,
    renderEmpty: renderEmpty && renderEmpty(query ?? ''),
  };
}
